import React, { useState, useEffect, useCallback, useMemo } from 'react'; 
import api, { getUserDetailsById, updateUserDetails } from '../api'; 
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts"; 

const API_BASE_URL = '/vres/users';
const PROJECTS_API_URL = '/vres/projects'; 

const ROLE_COLORS = {
    'ADMIN': "#dc3545",
    'PROJECT COORDINATOR': "#ffc107",
    'VENDOR': "#007bff",
    'ISSUER': "#17a2b8",
    'MAKER': "#28a745",
    'CHECKER': "#6f42c1",
    'NO ROLE': "#6c757d",
};

// --- NEW COMPONENT: Reset Password Modal --------------------------------------
const ResetPasswordModal = ({ userId, onClose }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      return toast.warn("Please fill in both fields.");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("Passwords do not match.");
    }
    if (newPassword.length < 6) {
        return toast.warn("Password must be at least 6 characters.");
    }

    setLoading(true);
    try {
      // API call to reset password
      await api.post(`${API_BASE_URL}/${userId}/reset-password`, { password: newPassword });
      toast.success("Password reset successfully! Email sent to user.");
      onClose(); // Close the reset popup
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlayHighZ}>
      <div style={styles.popup}>
        <h4 className="text-danger text-center mb-4">Reset Password</h4>
        
        <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
                <label className="font-weight-bold text-dark">Enter New Password</label>
                <input 
                type="password" 
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                required
                />
            </div>

            <div className="form-group mb-4">
                <label className="font-weight-bold text-dark">Confirm Password</label>
                <input 
                type="password" 
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                required
                />
            </div>

            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
                <button type="submit" className="btn btn-danger" disabled={loading}>
                    {loading ? "Processing..." : "Reset Password"}
                </button>
            </div>
        </form>
      </div>
    </div>
  );
};

// --- UPDATED: Edit User Modal -------------------------------------------------
const EditUserModal = ({ isModalOpen, closeModal, user, onSave }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "" 
      });
    }
    // Reset the internal modal state when the parent modal opens/closes
    if(!isModalOpen) setShowResetPassword(false);
  }, [user, isModalOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validId = user.userId || user.id;

    if (user && validId) {
      onSave(validId, formData);
    } else {
        console.error("Cannot update: User ID is missing in the data object", user);
        toast.error("Error: User ID is missing. Cannot update.");
    }
  };

  if (!isModalOpen || !user) return null;

  const validUserId = user.userId || user.id;

  return (
    <>
        <div style={styles.overlay}>
        <div className="card shadow-lg" style={styles.modalCard}>
            <div className="text-center mb-4">
                <h4 style={{ color: '#007bff', fontWeight: 'bold' }}>Edit User Details</h4>
            </div>

            <form onSubmit={handleSubmit}>
            {/* Name Field */}
            <div className="form-group mb-3">
                <label className="font-weight-bold text-dark">Name</label>
                <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{ padding: '10px' }}
                />
            </div>

            {/* Email Field */}
            <div className="form-group mb-3">
                <label className="font-weight-bold text-dark">Email (User ID)</label>
                <input
                type="email"
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{ padding: '10px' }}
                />
            </div>

            {/* Phone Field */}
            <div className="form-group mb-3">
                <label className="font-weight-bold text-dark">Mobile Number</label>
                <input
                type="text"
                className="form-control"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="Enter 10-digit mobile number"
                style={{ padding: '10px' }}
                />
            </div>

            {/* --- ADMIN ACTION: RESET PASSWORD BUTTON --- */}
            <div className="mb-4 pt-3 border-top">
                <button 
                    type="button" 
                    className="btn btn-outline-danger w-100"
                    onClick={() => setShowResetPassword(true)}
                >
                    <i className="bi bi-key-fill me-2"></i> Reset User Password
                </button>
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-center gap-3">
                <button 
                type="submit" 
                className="btn btn-primary px-4 me-2"
                style={{ minWidth: '120px' }}
                >
                Update
                </button>
                <button 
                type="button" 
                className="btn btn-secondary px-4"
                onClick={closeModal}
                style={{ minWidth: '120px' }}
                >
                Cancel
                </button>
            </div>
            </form>
        </div>
        </div>

        {/* --- Render Reset Password Modal on top if state is true --- */}
        {showResetPassword && (
            <ResetPasswordModal 
                userId={validUserId} 
                onClose={() => setShowResetPassword(false)} 
            />
        )}
    </>
  );
};

// --- CHART COMPONENT (Unchanged) --------------------------------------------------
const UserRoleChart = ({ data }) => {
    const hasData = data && data.some(d => d.value > 0);
    const totalUsers = data.reduce((sum, entry) => sum + entry.value, 0);

    return (
        <div className="card shadow-sm h-100 avoid-break" style={{ minWidth: '350px' }}>
            <div className="card-body">
                <h5 className="card-title text-center text-secondary mb-3">
                    User Role Distribution ({totalUsers} Total)
                </h5>
                {!hasData ? (
                    <p className="text-center text-muted py-5">No user data available for this project.</p>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={3}
                                labelLine={false}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={ROLE_COLORS[entry.name] || ROLE_COLORS['NO ROLE']} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [value, props.payload.name]} />
                            <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ paddingTop: '10px' }} />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

// --- MAIN PAGE COMPONENT ----------------------------------------
const UserDashboardPage = () => {
    const navigate = useNavigate(); 
    
    // --- State Management ---
    const [projects, setProjects] = useState([]); 
    const [selectedProjectId, setSelectedProjectId] = useState(''); 
    
    const [users, setUsers] = useState([]); 
    const [isLoading, setIsLoading] = useState(false); 
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Current user info
    const [currentUser, setCurrentUser] = useState(null);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('currentUser'));
        if (storedUser) {
            setCurrentUser(storedUser);
        }
    }, []);

    const handlePrint = () => {
        window.print();
    };

    // --- 1. Fetch Project List ---
    useEffect(() => {
        const fetchProjects = async () => {
            const storedUser = JSON.parse(localStorage.getItem("currentUser"));
            
            if (storedUser && storedUser.role === 'OBSERVER') {
                const assignedProjects = storedUser.projects || [];
                setProjects(assignedProjects);

                if (assignedProjects.length > 0) {
                      const pId = assignedProjects[0].projectId || assignedProjects[0].id;
                      setSelectedProjectId(pId);
                }
                return; 
            }

            try {
                const response = await api.get(PROJECTS_API_URL);
                setProjects(response.data);
            } catch (err) {
                console.error("Error fetching projects:", err);
                toast.error("Failed to load project list.");
            }
        };
        fetchProjects();
    }, []);

    // --- 2. Fetch Users ---
    const fetchDashboardData = useCallback(async () => {
        if (!selectedProjectId) {
            setUsers([]);
            return;
        }

        setIsLoading(true);
        try {
            const response = await api.get(`${API_BASE_URL}/dashboard`, {
                params: { projectId: selectedProjectId } 
            });
            setUsers(response.data);
            setError(null);
        } catch (err) {
            console.error("Error fetching user dashboard data:", err);
            setError("Failed to load user data.");
            toast.error("Failed to load user data.");
            
            if (err.response && err.response.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    }, [selectedProjectId, navigate]); 

    useEffect(() => {
        fetchDashboardData(); 
    }, [fetchDashboardData]); 

    // --- 3. Prepare Chart Data ---
    const roleChartData = useMemo(() => {
        const roleCounts = {};
        
        users.forEach(user => {
            const matchingAssignment = user.assignments && user.assignments.find(
                a => String(a.projectId) === String(selectedProjectId)
            );
            const roleName = matchingAssignment ? matchingAssignment.roleName.toUpperCase() : 'NO ROLE';
            roleCounts[roleName] = (roleCounts[roleName] || 0) + 1;
        });

        return Object.keys(roleCounts).map(roleName => ({
            name: roleName,
            value: roleCounts[roleName],
        })).sort((a, b) => b.value - a.value); 

    }, [users, selectedProjectId]); 

    // --- 4. Search Filter ---
    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm))
    );

    // --- 5. Handlers for Edit Actions ---
    const handleEditClick = async (userId) => {
        try {
            // Fetch fresh details before editing
            const res = await getUserDetailsById(userId);
            setSelectedUser(res.data);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error fetching user details:", error);
            toast.error("Failed to fetch user details for editing.");
        }
    };

    const handleUpdateUser = async (userId, data) => {
        try {
            await updateUserDetails(userId, data);
            toast.success(`User updated successfully!`);
            
            setIsModalOpen(false);
            setSelectedUser(null);

            // ✅ OPTIMISTIC UPDATE: Update UI instantly
            setUsers(prevUsers => prevUsers.map(user => 
                user.id === userId 
                    ? { ...user, ...data } // Merge old data with new edits
                    : user
            ));

            // Refresh background data
            fetchDashboardData(); 

        } catch (error) {
            console.error("Error updating user:", error);
            toast.error(error.response?.data?.message || "Failed to update user.");
        }
    };

    // --- Helper: Render Assignments ---
    const renderAssignments = (assignments) => {
        if (!assignments || assignments.length === 0) {
            return <span className="badge bg-secondary-subtle text-secondary fw-normal">None</span>;
        }
        
        return (
            <div style={{ fontSize: '0.85rem' }}>
                {assignments.map((assignment, index) => {
                    const isCurrentProject = String(assignment.projectId) === String(selectedProjectId);
                    return (
                        <div key={index} className="mb-1">
                            <span className={`badge ${isCurrentProject ? 'bg-primary' : 'bg-light text-muted border'} me-2 fw-normal`}>
                                {assignment.roleName}
                            </span>
                            <span className={isCurrentProject ? "text-dark small fw-bold" : "text-muted small"}>
                                ({assignment.projectName})
                            </span>
                        </div>
                    )
                })}
            </div>
        );
    };

    // --- Helper: Get Primary Role ---
    const getPrimaryRole = (assignments) => {
        if (!assignments) return <span className="text-muted fst-italic small">No Role</span>;

        const projectRole = assignments.find(a => String(a.projectId) === String(selectedProjectId));

        if (projectRole) {
            return <span className="badge bg-info text-dark fw-bold p-2">{projectRole.roleName}</span>;
        }
        return <span className="text-muted fst-italic small">N/A</span>;
    };

    // --- Main Render ---
    return (
        <div className="container-fluid py-4" style={{ position: 'relative' }}>
            <h2 className="mb-4 text-primary text-center">User Dashboard</h2>
            
            <div style={styles.headerArea} className="no-print">
                <label htmlFor="projectSelect" style={styles.headerTitle}>
                    {currentUser && currentUser.role === 'OBSERVER' 
                        ? "Assigned Project" 
                        : "Select Project to View Users"}
                </label>
                
                <select 
                    id="projectSelect"
                    style={styles.centeredSelect}
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    disabled={currentUser && currentUser.role === 'OBSERVER' && projects.length === 1}
                >
                    <option value="">-- Choose a Project --</option>
                    {projects.map((proj) => (
                        <option key={proj.projectId || proj.id} value={proj.projectId || proj.id}>
                            {proj.projectName || proj.title}
                        </option>
                    ))}
                </select>

                {selectedProjectId && (
                    <button onClick={handlePrint} style={styles.printButton}>
                        <i className="bi bi-printer me-2"></i> Print Report
                    </button>
                )}
            </div>

            {!selectedProjectId ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-arrow-up-circle display-4"></i>
                    <p className="mt-3 fs-5">Please select a project above.</p>
                </div>
            ) : (
                <>
                    {isLoading && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2">Loading project data...</p>
                        </div>
                    )}

                    {error && <div className="alert alert-danger text-center">{error}</div>}

                    {!isLoading && !error && (
                        <>
                            {/* Stats & Chart */}
                            <div className="row mb-5">
                                <div className="col-lg-6 col-md-12 mb-4">
                                    <div className="card shadow-sm h-100 p-4 avoid-break">
                                        <h5 className="card-title text-secondary mb-3">Project Overview Stats</h5>
                                        <p className="text-muted">
                                            Showing user data specifically for Project ID: <strong>{selectedProjectId}</strong>
                                        </p>
                                        <h3 className="mt-3">
                                            <span className="badge bg-success-subtle text-success p-3">
                                                Total Users in Project: <span className="fw-bold">{users.length}</span>
                                            </span>
                                        </h3>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-12 mb-4">
                                    <UserRoleChart data={roleChartData} />
                                </div>
                            </div>

                            <hr/>
                            <h3 className="mb-3 text-secondary">User List Details</h3>

                            <div className="mb-4 no-print">
                                <input
                                    type="text"
                                    className="form-control shadow-sm"
                                    placeholder="Search by Name, Email, or Phone..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="card shadow-lg mb-4 border-0">
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-striped table-hover align-middle mb-0">
                                            <thead className="bg-primary text-white">
                                                <tr>
                                                    <th style={{ width: '5%' }} className="text-center border-0">ID</th>
                                                    <th style={{ width: '15%' }} className="border-0">Name</th>
                                                    <th style={{ width: '15%' }} className="border-0">Email</th>
                                                    <th style={{ width: '10%' }} className="border-0">Phone</th>
                                                    <th style={{ width: '10%' }} className="border-0">Project Role</th> 
                                                    <th style={{ width: '20%' }} className="border-0">All Assignments</th>
                                                    <th style={{ width: '15%' }} className="border-0">Vendor Details</th>
                                                    
                                                    {/* ✅ CONDITIONAL HEADER: Only Admin sees Actions */}
                                                    {currentUser?.role === 'ADMIN' && (
                                                        <th style={{ width: '10%' }} className="text-center border-0">Actions</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsers.map((user) => (
                                                    <tr key={user.id}>
                                                        <td className="text-center text-muted small">{user.id}</td>
                                                        <td className="fw-semibold">{user.name}</td>
                                                        <td>{user.email}</td>
                                                        <td>{user.phone || <span className="text-muted fst-italic">N/A</span>}</td> 
                                                        <td> 
                                                            {getPrimaryRole(user.assignments)}
                                                        </td>
                                                        <td className="small">
                                                            {renderAssignments(user.assignments)}
                                                        </td>
                                                        <td className="small">
                                                            <div><span className="fw-bold text-dark">GST:</span> {user.vendorGst || 'N/A'}</div>
                                                            <div className="text-wrap"><span className="fw-bold text-dark">Address:</span> {user.vendorAddress || 'N/A'}</div>
                                                        </td>
                                                        
                                                        {/* ✅ CONDITIONAL BODY: Only Admin can see/click Edit button */}
                                                        {currentUser?.role === 'ADMIN' && (
                                                            <td className="text-center">
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => handleEditClick(user.id)}
                                                                    title="Edit User"
                                                                >
                                                                    Edit
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                                {filteredUsers.length === 0 && (
                                                    <tr>
                                                        <td colSpan={currentUser?.role === 'ADMIN' ? 8 : 7} className="text-center text-muted py-3">
                                                            No users found for this project matching your search.
                                                        </td> 
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            <EditUserModal
                isModalOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                user={selectedUser}
                onSave={handleUpdateUser}
            />
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center',
        alignItems: 'center', zIndex: 1050
    },
    overlayHighZ: { // Higher Z-Index for nested modal
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center',
        alignItems: 'center', zIndex: 1100 
    },
    modalCard: {
        width: '100%', maxWidth: '500px', backgroundColor: '#fff',
        borderRadius: '8px', border: 'none', padding: '25px'
    },
    popup: {
        backgroundColor: "#fff", padding: "25px", borderRadius: "8px", width: "400px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.3)"
    },
    // Shared Layout Styles
    headerArea: {
      width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', 
      marginBottom: '30px', padding: '0 20px', textAlign: 'center',
    },
    headerTitle: {
      margin: '0 0 15px 0', color: '#333', fontWeight: 'bold', fontSize: '1.5rem',
    },
    centeredSelect: {
      width: "100%", maxWidth: "350px", padding: "10px 15px", borderRadius: "6px",
      fontSize: "16px", border: "1px solid #ced4da", backgroundColor: "white",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)", cursor: "pointer",
      marginBottom: "15px", textAlign: "center"
    },
    printButton: {
      backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '8px 24px',
      borderRadius: '5px', cursor: 'pointer', fontSize: '16px', display: 'flex',
      alignItems: 'center', gap: '8px', boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      transition: 'background-color 0.2s',
    },
};

export default UserDashboardPage;